import { ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../common/Header/Header';
import {
  Buttons,
  Page,
  TableWrapper,
} from '../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import Button from '../../components/Button/Button';
import { Variant } from '../../components/Button/Button.types';
import Table from '../../components/Table/Table';
import { useGetBases } from '../../services/base/base.service';
import { Base } from '../../services/base/base.types';
import { useFetchErrorToast } from '../../utils/useFetchErrorToasts/useFetchErrorToast';
import { CSVLink } from 'react-csv';
import Divider from '@mui/material/Divider';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const Bases: FC = () => {
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact>(null);
  const [selectedRow, setSelectedRow] = useState<Base | undefined>(undefined);
  const { data, isError: basesError } = useGetBases();
  const muiTheme = createTheme();

  const allBases = data?.data || [];

  const handleRowSelection = () => {
    const selectedRows = gridRef?.current?.api.getSelectedRows();
    setSelectedRow(selectedRows?.[0]);
  };

  const handleOpenClick = () => {
    navigate(`/tables/bases/${selectedRow?.baseNo}`);
  };

  const headers = [
    { label: 'MOA Base No.', key: 'baseNo' },
    { label: 'Base No.', key: 'baseNo' },
    { label: 'Description', key: 'description' },
    { label: 'Status No.', key: 'statusNo' },
    { label: 'Mnemonic', key: 'mnemonic' },
    { label: 'Seq No.', key: 'seqNo' },
    { label: 'Long Description', key: 'longDescription' },
    { label: 'Synopsis', key: 'synopsis' },
    { label: 'Valid on Web', key: 'validOnWeb' },
    { label: 'Valid in Order Entry', key: 'displayInOe' },
    { label: 'BOH Description', key: 'displayDesc' },
    { label: 'Created Date', key: 'createdDate' },
    { label: 'Updated Date', key: 'updatedDate' },
    { label: 'Created By', key: 'createdBy' },
    { label: 'Updated By', key: 'updatedBy' },
  ];

  const csvData = allBases.map((base: Base) => ({
    baseNo: base.baseNo,
    description: base.description,
    statusNo: base.statusNo,
    mnemonic: base.mnemonic,
    seqNo: base.seqNo,
    longDescription: base.longDescription,
    synopsis: base.synopsis,
    validOnWeb: base.validOnWeb ? 'Y' : 'N',
    displayInOe: base.displayInOe ? 'Y' : 'N',
    displayDesc: base.displayDesc,
  }));

  useFetchErrorToast([{ isError: basesError, label: 'Error fetching bases', id: 'basesFetchError' }]);

  return (
    <>
      <Header breadcrumb={{ to: '/tables', label: 'All tables' }} title='Base' />
      <Page>
        <TableWrapper>
          <Table
            rowData={data?.data}
            ref={gridRef}
            returnedRecords={data?.data?.length}
            height={'100%'}
            rowSelection='single'
            onRowSelected={handleRowSelection}
            columnDefs={[
              {
                minWidth: 48,
                field: '',
                headerName: '',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
              },
              { minWidth: 80, field: 'baseNo', headerName: 'MOA Base No.' },
              { minWidth: 80, field: 'baseNo', headerName: 'Base No.' },
              { minWidth: 112, field: 'description', headerName: 'Description' },
              { minWidth: 80, field: 'statusNo', headerName: 'Status No.' },
              { minWidth: 80, field: 'mnemonic', headerName: 'Mnemonic' },
              { minWidth: 80, field: 'seqNo', headerName: 'Seq No.' },
              { minWidth: 180, field: 'longDescription', headerName: 'Long Description' },
              { minWidth: 112, field: 'synopsis', headerName: 'Synopsis' },
              {
                minWidth: 80,
                field: 'validOnWeb',
                headerName: 'Valid on Web',
                valueFormatter: (params) => (!params.value ? 'N' : 'Y'),
                filterValueGetter: (params: ValueGetterParams) => (!params.getValue('validOnWeb') ? 'N' : 'Y'),
                floatingFilterComponentParams: {
                  suppressFilterButton: true,
                  inputAttributes: { 'aria-label': 'Valid on Web Filter Input' },
                },
              },
              {
                minWidth: 112,
                field: 'displayInOe',
                headerName: 'Valid in Order Entry',
                valueFormatter: (params) => (!params.value ? 'N' : 'Y'),
                filterValueGetter: (params: ValueGetterParams) => (!params.getValue('displayInOe') ? 'N' : 'Y'),
                floatingFilterComponentParams: {
                  suppressFilterButton: true,
                  inputAttributes: { 'aria-label': 'Valid in Order Entry Filter Input' },
                },
              },
              { minWidth: 112, field: 'displayDesc', headerName: 'BOH Description' },
            ]}
          />
        </TableWrapper>
        <Buttons>
          <Button
            label='Open'
            variant={Variant.Primary}
            onClick={handleOpenClick}
            disabled={selectedRow === undefined}
          />
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            <Divider orientation='horizontal' variant={'fullWidth'} flexItem />{' '}
          </ThemeProvider>
          <CSVLink
            data={csvData}
            headers={headers}
            filename={`Bases_${new Date().toISOString().split('T')[0]}.csv`}
            style={{ textDecoration: 'none', color: 'inherit' }}
            asyncOnClick={true}
            onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
              return new Promise<void>(resolve => {
                setTimeout(() => {
                  resolve();
                }, 500);
              });
            }}
          >
            <Button variant={Variant.Secondary} label='Export to CSV' />
          </CSVLink>
        </Buttons>
      </Page>
    </>
  );
};

export default Bases;
